import type { FC } from 'react';
import { memo, useEffect, useRef } from 'react';

import { useTranslation } from '../../../api/useTranslation';
import { useConfigSlice } from '../../../store/slices/config/selectors';
import { useHelpSlice } from '../../../store/slices/help/selectors';
import { useSettingsSlice } from '../../../store/slices/settings/selectors';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import type { HelpPageConnectedProps } from '../types';
import { HelpText } from '../utils/HelpText';

import styles from './HelpPage.module.css';

const HelpPageConnectedComponent: FC<HelpPageConnectedProps> = ({ helpConfig, selectedHelpPage }) => {
  const helpFile = useHelpSlice.getHelpFile();
  const helpPageRef = useRef<HTMLDivElement>(null);
  const tableNameKey = useConfigSlice.tableConfig()?.tableName ?? '';
  const isRTL = useSettingsSlice.getIsCurrentLanguageRTL();

  const { translations } = useTranslation();
  const tableName = translations[tableNameKey] || tableNameKey;

  useEffect(() => {
    const resetScroll = (): void => {
      helpPageRef.current?.scrollTo({ top: 0, behavior: 'auto' });
    };

    if (helpPageRef.current) {
      resetScroll();
    }

    return () => {
      resetScroll();
    };
  }, [selectedHelpPage]);

  const mods = useModifiers(styles, 'helpPage', { rtl: isRTL });

  // Do not mount component if no page has been selected or required data is not available
  if (!selectedHelpPage || !helpConfig[selectedHelpPage]) {
    return null;
  }

  const helpFileTitle = helpFile?.[selectedHelpPage]?.title;
  const HelpPageContent: FC = helpConfig[selectedHelpPage];

  return (
    <div className={`${styles.helpPage} ${mods}`} ref={helpPageRef}>
      <div>
        <div className={styles.helpPage__title}>
          <HelpText dynamicValues={{ TABLE_NAME: tableName }}>{String(helpFileTitle)}</HelpText>
        </div>
        <div className={styles.helpPage__content}>
          <HelpPageContent />
        </div>
      </div>
    </div>
  );
};

HelpPageConnectedComponent.displayName = 'HelpPageConnected';

export const HelpPageConnected = memo(HelpPageConnectedComponent);
